import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useCallback, useRef, useEffect, } from "react";
import { useMutation } from "@tanstack/react-query";
import { mvpApiTaskActivityMutation } from "@labeling-tools/api/lebron/generated/@tanstack/react-query.gen";
const IdleTimeContext = createContext(null);
const DEFAULT_EVENTS = [
    "mousemove",
    "mousedown",
    "keydown",
    "keyup",
    "touchstart",
    "touchmove",
    "wheel",
    "scroll",
];
/** Interval in ms to send activity heartbeat to server */
const ACTIVITY_HEARTBEAT_INTERVAL = 30000; // 30 seconds
export const IdleTimeProvider = ({ children, taskId, timeout = 60000, events = DEFAULT_EVENTS, onIdle, onActive, }) => {
    const [isIdle, setIsIdle] = useState(false);
    const [idleTime, setIdleTime] = useState(0);
    const [taskExpired, setTaskExpired] = useState(false);
    const lastActivityRef = useRef(Date.now());
    const timeoutRef = useRef(null);
    const intervalRef = useRef(null);
    const heartbeatIntervalRef = useRef(null);
    // Mutation to update task last_renewed_at timestamp
    const { mutate: sendActivityHeartbeat } = useMutation(mvpApiTaskActivityMutation());
    // Store callbacks in refs to avoid effect re-runs
    const onIdleRef = useRef(onIdle);
    const onActiveRef = useRef(onActive);
    useEffect(() => {
        onIdleRef.current = onIdle;
    }, [onIdle]);
    useEffect(() => {
        onActiveRef.current = onActive;
    }, [onActive]);
    const startIdleTimer = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
            setIsIdle(true);
            onIdleRef.current?.();
        }, timeout);
    }, [timeout]);
    const reset = useCallback(() => {
        lastActivityRef.current = Date.now();
        setIdleTime(0);
        setIsIdle(false);
        onActiveRef.current?.();
        startIdleTimer();
    }, [startIdleTimer]);
    const handleActivity = useCallback(() => {
        if (!timeoutRef.current)
            return;
        lastActivityRef.current = Date.now();
        setIdleTime(0);
        startIdleTimer();
    }, [startIdleTimer]);
    // Set up event listeners and timers
    useEffect(() => {
        events.forEach((event) => {
            window.addEventListener(event, handleActivity, { passive: true });
        });
        startIdleTimer();
        intervalRef.current = setInterval(() => {
            const elapsed = Date.now() - lastActivityRef.current;
            setIdleTime(elapsed);
        }, 1000);
        return () => {
            events.forEach((event) => {
                window.removeEventListener(event, handleActivity);
            });
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [events, handleActivity, startIdleTimer]);
    // Send activity heartbeat every 30 seconds when not idle
    useEffect(() => {
        // Clear any existing heartbeat interval
        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
        }
        // Only send heartbeats when not idle and we have a taskId
        if (!isIdle && taskId) {
            const sendHeartbeat = () => {
                console.log("[IdleTimeContext] Sending activity heartbeat for task:", taskId);
                sendActivityHeartbeat({ path: { task_id: taskId } });
            };
            // Send immediately when becoming active
            sendHeartbeat();
            // Then send every 30 seconds
            heartbeatIntervalRef.current = setInterval(sendHeartbeat, ACTIVITY_HEARTBEAT_INTERVAL);
        }
        return () => {
            if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current);
            }
        };
    }, [isIdle, taskId, sendActivityHeartbeat]);
    // Expose modal triggers on window.dev in dev mode
    useEffect(() => {
        if (process.env.NODE_ENV === "production")
            return;
        const devModals = {
            /** Open "Are you still there?" warning modal */
            idle: () => setIsIdle(true),
            /** Open "Task No Longer Available" exit modal */
            taskExpired: () => setTaskExpired(true),
            /** Reset all modal state */
            reset: () => {
                reset();
                setTaskExpired(false);
                window.dispatchEvent(new CustomEvent("dev:reset-modals"));
            },
        };
        const win = window;
        win.dev = { ...win.dev, modals: devModals };
        return () => {
            if (win.dev && typeof win.dev === "object") {
                delete win.dev.modals;
            }
        };
    }, [reset, setTaskExpired]);
    return (_jsx(IdleTimeContext.Provider, { value: {
            isIdle,
            idleTime,
            taskExpired,
            setTaskExpired,
            reset,
        }, children: children }));
};
export const useIdleTimeContext = () => {
    const context = useContext(IdleTimeContext);
    if (!context) {
        throw new Error("useIdleTimeContext must be used within an IdleTimeProvider");
    }
    return context;
};
